import { useState } from "react";
import { Link } from "react-router-dom";
import { Package, Search, Truck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { http, formatPrice } from "@/lib/api";

const statusStyle = {
  Ordered: "bg-[#e7f0ff] text-[#A01F1F]",
  Packed: "bg-[#fef9c3] text-[#a16207]",
  Shipped: "bg-[#f3e8ff] text-[#7e22ce]",
  Delivered: "bg-[#dcfce7] text-[#15803d]",
  Cancelled: "bg-[#fee2e2] text-[#b91c1c]",
};

const MyOrders = () => {
  const [phone, setPhone] = useState("");
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 10) return toast.error("Enter a valid 10-digit phone number");
    setBusy(true);
    try {
      const { data } = await http.get("/my-orders", { params: { phone: clean } });
      setOrders(data.orders);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-[900px] mx-auto px-3 sm:px-4 py-6" data-testid="my-orders-page">
      <div className="bg-white rounded-sm fk-shadow p-5">
        <h1 className="font-head text-xl font-bold text-[#212121] flex items-center gap-2">
          <Package className="w-5 h-5 text-[#C62828]" /> Track My Orders
        </h1>
        <p className="text-sm text-[#878787] mt-1">
          No account needed — enter the phone number you used at checkout.
        </p>
        <form onSubmit={lookup} className="flex gap-2 mt-4 max-w-md">
          <div className="flex items-center gap-2 border border-[#c2c2c2] rounded-sm px-3 h-11 text-sm text-[#555] bg-[#f7f7f7]">+91</div>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="10-digit mobile number"
            inputMode="numeric"
            className="flex-1 h-11 border border-[#c2c2c2] rounded-sm px-3 text-sm outline-none focus:border-[#C62828]"
            data-testid="my-orders-phone-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="flex items-center gap-1.5 bg-[#C62828] text-white font-semibold px-5 rounded-sm disabled:opacity-60"
            data-testid="my-orders-search-btn"
          >
            <Search className="w-4 h-4" /> {busy ? "…" : "Find"}
          </button>
        </form>
      </div>

      {orders !== null && (
        <div className="mt-4 space-y-3 fk-fade-up">
          {orders.length === 0 ? (
            <div className="bg-white rounded-sm fk-shadow py-14 text-center text-[#878787]" data-testid="my-orders-empty">
              No orders found for this number.
            </div>
          ) : (
            orders.map((o) => (
              <Link
                to={`/order/${o.id}`}
                key={o.id}
                className="block bg-white rounded-sm fk-shadow p-4 fk-card-hover"
                data-testid={`my-order-${o.id}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-semibold text-sm text-[#212121]">{o.id}</p>
                    <p className="text-xs text-[#878787]">
                      {new Date(o.created_at).toLocaleDateString("en-IN")} · {o.items.length} item(s) · {formatPrice(o.payable)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-sm ${statusStyle[o.status] || ""}`}>
                      {o.status}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#878787]" />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {o.items.slice(0, 5).map((it) => (
                    <img key={it.id} src={it.image_url} alt="" className="w-10 h-10 object-contain bg-[#f7f7f7] rounded-sm border border-[#eee]" />
                  ))}
                  <span className="text-xs text-[#388e3c] font-medium flex items-center gap-1 ml-auto">
                    <Truck className="w-3.5 h-3.5" /> {o.status === "Delivered" ? "Delivered" : `Arriving ${o.est_delivery}`}
                  </span>
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </main>
  );
};

export default MyOrders;
